import { firstValueFrom } from 'rxjs';
import omit from 'lodash/omit';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import AbstractIframeCheckout from '@bambora/checkout-sdk-web/dist/types/abstractIframeCheckout';
import { CustomComponent, IUserCompany, ngHelper } from '@cakeiteasy/web-utils';

import { IPaymentInfo, IWebhookUrls } from '../..';
import { PaymentApiService } from '../../../api-services/services/payment-api.service';
import { ICart } from '../../../cart';
import { IUser } from '../../../users';
import { PAYMENT_PERSON_TYPES, PAYMENT_PLATFORM_TYPES } from '../../payment.constants';
import { BamboraCheckoutService } from '../../services/bambora-checkout.service';
import { PaymentStorageService } from '../../services/payment-storage.service';

@Component({
  standalone: false,
  selector: 'bambora-payment',
  templateUrl: './bambora-payment.component.html',
  styleUrls: ['./bambora-payment.component.scss'],
})
export class BamboraPaymentComponent extends CustomComponent implements OnChanges, OnDestroy {
  @Input() personType: string;
  @Input() user: IUser;
  @Input() countryCode: string;
  @Input() cart: ICart;
  @Input() queueProcessing: Promise<any>;
  @Input() webhookUrls: IWebhookUrls;
  @Input() rejectUrl: string;
  @Input() giftCardCode: string = null;
  @Input() embed = false;
  @Input() termsClickFn: any;
  @Input() cancelPaymentOnClose = false;
  @Input() useCompanySearcher = false;
  @Input() bakeryId: number;

  @ViewChild('embedContainer', { static: false }) embedContainer: ElementRef<HTMLDivElement>;

  @Output() onSuccess = new EventEmitter<string>();
  @Output() onFail = new EventEmitter<any>();
  @Output() onTermsClick = new EventEmitter<void>();
  @Output() onError = new EventEmitter<any>();
  @Output() onOrderCreate = new EventEmitter<string>();
  @Output() onInfoUpdated = new EventEmitter<IPaymentInfo>();
  @Output() onCompanySelected = new EventEmitter<IUserCompany>();
  @Output() sessionActivityChanged = new EventEmitter<boolean>();
  @Output() onBusy = new EventEmitter<Promise<any>>();

  public PAYMENT_PERSON_TYPES = PAYMENT_PERSON_TYPES;
  public isValid: boolean;
  public info: IPaymentInfo;
  public isSessionActive = false;

  public checkout: AbstractIframeCheckout<any>;
  public alreadySubscriber = false;

  constructor(
    private _paymentApi: PaymentApiService,
    private _storage: PaymentStorageService,
    private _bamboraCheckout: BamboraCheckoutService,
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges) {
    const { bambora } = PAYMENT_PLATFORM_TYPES;
    ngHelper.ifChanged(changes, 'user', (user: IUser, prevUser: IUser) => {
      if (user) {
        if (user.subscribedOnNews) {
          this.alreadySubscriber = true;
        }
        this._storage.updateContactInfo(bambora, null, user, this.useCompanySearcher);
      }
      this.info = this._storage.getContactInfo(bambora);
    });

    ngHelper.ifChanged(changes, 'queueProcessing', (queueProcessing) => {
      if (!this.embed || !this.isSessionActive) {
        return;
      }

      const promise$ = queueProcessing.then(() => {
        if (this.checkout) {
          this.checkout.destroy();
          return this.submit(null);
        }
      });

      this.onBusy.emit(promise$);
    });
  }

  public ngOnDestroy() {
    super.ngOnDestroy();

    if (this.checkout) {
      this.checkout.destroy();
    }
  }

  private _getPaymentPayload(info: IPaymentInfo, code?: string) {
    let payload = { ...info, code };

    if (this.personType === PAYMENT_PERSON_TYPES.PRIVATE) {
      payload = omit(payload, ['userCompanyName', 'invoiceCompanyId', 'invoiceCompanyOrgNumber']);
    }

    return {
      ...payload,
      ...this.webhookUrls,
    };
  }

  public cancelSession() {
    this.checkout.destroy();
    this.isSessionActive = false;
    this.sessionActivityChanged.emit(false);
  }

  public confirm($event) {
    const promise = this.queueProcessing || Promise.resolve(null);
    this.onBusy.emit(promise.then(() => this.submit($event)));
  }

  public submit($event) {
    const payload = this._getPaymentPayload(this.info, this.giftCardCode);

    return firstValueFrom(this._paymentApi.createBamboraPayment(payload))
      .then((data: { paymentId: number; sid: string; token: string; url: string }) => {
        this.onOrderCreate.emit(data.sid);
        this.isSessionActive = true;
        this.sessionActivityChanged.emit(true);

        const onError = (error: any) => {
          this.onError.emit(error);
        };

        this.checkout = null;
        if (this.embed) {
          this.checkout = this._bamboraCheckout.creteInlineCheckout(
            data,
            onError.bind(this),
            this.embedContainer.nativeElement,
          );
        } else {
          this.checkout = this._bamboraCheckout.createCheckout(data, onError.bind(this), $event);
        }

        const onSuccess = (payload) => {
          this.onSuccess.emit(payload.acceptUrl);
        };

        const onCancel = (payload) => {
          if (this.cancelPaymentOnClose) {
            firstValueFrom(this._paymentApi.cancelPayment(data.paymentId));
          }
        };

        this._bamboraCheckout.subscribeOnEvents(this.checkout, onSuccess.bind(this), onCancel.bind(this));
      })
      .catch((err) => {
        this.onFail.emit(err);
      });
  }

  public newsletterUpdate(isSubscribed: boolean) {
    const { bambora } = PAYMENT_PLATFORM_TYPES;
    this.info.subscribedOnNews = isSubscribed;
    this._storage.updateContactInfo(bambora, this.info);
  }

  public infoUpdated(paymentInfo: IPaymentInfo) {
    const { bambora } = PAYMENT_PLATFORM_TYPES;
    this.info = { ...this.info, ...paymentInfo };

    if (this.personType === PAYMENT_PERSON_TYPES.COMPANY && this.useCompanySearcher) {
      this.isValid = this.isValid && !!this.info.userCompanyName;
    }

    this._storage.updateContactInfo(bambora, this.info);
  }
}
